import { useTranslation } from 'react-i18next';

import type { Locale, LocalizedText } from '../api/types';
import { changeLocale } from '../i18n';
import {
  countryFlag,
  countryName,
  formatCompact,
  formatDate,
  formatDateTime,
  formatNumber,
  formatPercent,
  formatRelative,
  formatSigned,
  pickText,
} from './format';

/**
 * One hook for translation plus locale-aware formatting, so components never
 * have to thread the current locale through by hand.
 */
export function useLocale() {
  const { t, i18n } = useTranslation();
  const locale: Locale = i18n.language?.startsWith('en') ? 'en' : 'zh';

  return {
    t,
    locale,
    setLocale: changeLocale,
    text: (value: LocalizedText | null | undefined) => pickText(value, locale),
    number: (value: number) => formatNumber(value, locale),
    compact: (value: number) => formatCompact(value, locale),
    signed: (value: number) => formatSigned(value, locale),
    percent: (value: number, digits?: number) => formatPercent(value, locale, digits),
    date: (value: string | null | undefined) => formatDate(value, locale),
    dateTime: (value: string | null | undefined) => formatDateTime(value, locale),
    relative: (value: string | null | undefined) => formatRelative(value, locale),
    flag: countryFlag,
    country: (code: string | null | undefined) => countryName(code, locale),
  };
}
